import React, { useRef, useState, useEffect } from "react";
import '../css/d3.css';
import { select, line, scaleLinear, axisBottom, axisLeft, scaleTime } from "d3";
import { Tooltip } from 'react-tooltip';

export default function D3Sentiment(props) {
  const svgRef = useRef();
  const boundingRef = useRef();
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  // --- FIX: support either an array or an object with a .data array ---
  const rawData = Array.isArray(props.data)
    ? props.data
    : Array.isArray(props.data?.data)
      ? props.data.data
      : [];
  const data = rawData
    .map(d => ({ ...d, dateObj: new Date(d.date) }))
    .sort((a, b) => a.dateObj - b.dateObj);

  // preserve your existing logic for filling in empty trailing points
  var emptyDataEnd = [];
  for (let i = data.length - 1; i >= 0; i--) {
    if (!data[i].high && !data[i].avg && !data[i].low) {
      emptyDataEnd.push({ date: data[i].date, val: 0 });
    } else {
      if (emptyDataEnd.length > 0) {
        let toUse = 0;
        toUse = data[i].high ? data[i].high : toUse;
        toUse = data[i].low  ? data[i].low  : toUse;
        toUse = data[i].avg  ? data[i].avg  : toUse;
        emptyDataEnd.push({ date: data[i].date, val: toUse });
      }
      emptyDataEnd.reverse();
      break;
    }
  }

  useEffect(() => {
    const handleWindowResize = () => {
      const w = boundingRef.current.offsetWidth;
      const h = boundingRef.current.offsetHeight;
      setWidth(w);
      setHeight(h);
      svgRef.current.style.width  = `${w}px`;
      svgRef.current.style.height = `${h}px`;
    };
    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  function getFormattedDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day   = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  }

  function numberWithCommas(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  useEffect(() => {
    const svg = select(svgRef.current);
    svg.selectAll("*").remove();

    var scaleOffset = props.small ? { y: 10, x: 10 } : { y: 30, x: 20 };

    const dates = data.map(d => new Date(d.date));
    const xMin  = new Date(Math.min(...dates));
    const xMax  = new Date(Math.max(...dates));

    const xScale = scaleTime()
      .domain([xMin, xMax])
      .range([scaleOffset.y, width - scaleOffset.x]);

    const yScale = scaleLinear()
      .domain([-1, 1])
      .range([height - scaleOffset.y, scaleOffset.x]);

    const xAxis = axisBottom(xScale)
      .ticks(7)
      .tickFormat(d => getFormattedDate(d));

    const yAxis = axisLeft(yScale);

    svg.append("path")
      .datum(emptyDataEnd)
      .attr("class", "line")
      .attr("d", line()
        .x(d => xScale(new Date(d.date)))
        .y(d => yScale(d.val))
      )
      .style("stroke", "var(--steelblue)")
      .style("stroke-dasharray", "3, 3")
      .style("fill", "none");

    let counter = 0;

    // High
    svg.append("path")
      .datum(data)
      .attr("class", "line")
      .attr("d", line()
        .x((d, i) => d.high ? xScale(new Date(d.date)) : xScale(new Date(data[counter].date)))
        .y((d, i) => {
          if (d.high) { counter = i; return yScale(d.high); }
          return yScale(data[counter].high ? data[counter].high : 0);
        })
      )
      .style("stroke", "var(--green)")
      .style("fill", "none");

    // Avg
    counter = 0;
    svg.append("path")
      .datum(data)
      .attr("class", "line")
      .attr("d", line()
        .x((d, i) => d.avg ? xScale(new Date(d.date)) : xScale(new Date(data[counter].date)))
        .y((d, i) => {
          if (d.avg) { counter = i; return yScale(d.avg); }
          return yScale(data[counter].avg ? data[counter].avg : 0);
        })
      )
      .style("stroke", "var(--blue)")
      .style("fill", "none");

    // Low
    counter = 0;
    svg.append("path")
      .datum(data)
      .attr("class", "line")
      .attr("d", line()
        .x((d, i) => d.low ? xScale(new Date(d.date)) : xScale(new Date(data[counter].date)))
        .y((d, i) => {
          if (d.low) { counter = i; return yScale(d.low); }
          return yScale(data[counter].low ? data[counter].low : 0);
        })
      )
      .style("stroke", "var(--red)")
      .style("fill", "none");

    if (!props.small) {
      svg.append("g")
        .attr("class", "x-axis")
        .attr("transform", `translate(0,${height - 30})`)
        .call(xAxis);

      svg.append("g")
        .attr("class", "y-axis")
        .attr("transform", "translate(30,0)")
        .attr("data-tooltip-id", "d3-sentiment-tooltip")
        .attr("data-tooltip-html", "Sentiment Score (-1/bearish to 1/bullish)")
        .call(yAxis);

      // bubbles (High / Avg / Low)
      svg.selectAll(".hbubble")
        .data(data)
        .enter()
        .append("a")
        .attr("xlink:href", d => d.hlink)
        .attr("target", "_blank")
        .attr("data-tooltip-id", "d3-sentiment-tooltip")
        .attr("data-tooltip-html", d => (d.high ? `High Sentiment: ${d.high.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0]} on ${getFormattedDate(new Date(d.date))}<br /><p style="width: 300px">${d.hsum}</p>` : ""))
        .append("circle")
        .attr("class", "bubble")
        .attr("cx", d => xScale(new Date(d.date)))
        .attr("cy", d => yScale(d.high))
        .attr("r", 5)
        .style("fill", "var(--green)")
        .style("visibility", d => (d.high ? "visible" : "hidden"));

      svg.selectAll(".abubble")
        .data(data)
        .enter()
        .append("a")
        .attr("xlink:href", d => d.alink)
        .attr("target", "_blank")
        .attr("data-tooltip-id", "d3-sentiment-tooltip")
        .attr("data-tooltip-html", d => (d.avg ? `Average Sentiment: ${d.avg.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0]} on ${getFormattedDate(new Date(d.date))}<br /><p style="width: 300px">${d.asum}</p>` : ""))
        .append("circle")
        .attr("class", "bubble")
        .attr("cx", d => xScale(new Date(d.date)))
        .attr("cy", d => yScale(d.avg))
        .attr("r", 5)
        .style("fill", "var(--blue)")
        .style("visibility", d => (d.avg ? "visible" : "hidden"));

      svg.selectAll(".lbubble")
        .data(data)
        .enter()
        .append("a")
        .attr("xlink:href", d => d.llink)
        .attr("target", "_blank")
        .attr("data-tooltip-id", "d3-sentiment-tooltip")
        .attr("data-tooltip-html", d => (d.low ? `Low Sentiment: ${d.low.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0]} on ${getFormattedDate(new Date(d.date))}<br /><p style="width: 300px">${d.lsum}</p>` : ""))
        .append("circle")
        .attr("class", "bubble")
        .attr("cx", d => xScale(new Date(d.date)))
        .attr("cy", d => yScale(d.low))
        .attr("r", 5)
        .style("fill", "var(--red)")
        .style("visibility", d => (d.low ? "visible" : "hidden"));
    }
  }, [data, emptyDataEnd, width, height, props.small]);

  const boundingStyle = props.small
    ? { width: "90%", height: "80px" }
    : { width: "100%", height: "calc(80vh - 175px)" };

  return (
    <div ref={boundingRef} style={boundingStyle}>
      {!props.small && (
        <div className="insider-data">
          <p style={{ marginBottom: 0 }}><b>Insider Trading Data (cumulative net shares):</b></p>
          <p style={{ margin: 0 }}>
            {String((new Date().getFullYear()) - 1) + ": " + (props.insider_trading_data.previous_year_percent >= 0
              ? `Transactors bought ${(Math.round(Math.abs(props.insider_trading_data.previous_year_percent) * 10000) / 100).toFixed(2)}% more shares`
              : `Transactors sold ${(Math.round(Math.abs(props.insider_trading_data.previous_year_percent) * 10000) / 100).toFixed(2)}% of shares`)}
            {" (" + (props.insider_trading_data.previous_year_shares[1] !== 0
              ? `${numberWithCommas(Math.round(props.insider_trading_data.previous_year_shares[1]))} to ${numberWithCommas(Math.round(props.insider_trading_data.previous_year_shares[1] + props.insider_trading_data.previous_year_shares[0]))}`
              : "0 transactions") + ")"}
          </p>
          <p style={{ margin: 0 }}>
            {String((new Date().getFullYear())) + ": " + (props.insider_trading_data.current_year_percent >= 0
              ? `Transactors bought ${(Math.round(Math.abs(props.insider_trading_data.current_year_percent) * 10000) / 100).toFixed(2)}% more shares`
              : `Transactors sold ${(Math.round(Math.abs(props.insider_trading_data.current_year_percent) * 10000) / 100).toFixed(2)}% of shares`)}
            {" (" + (props.insider_trading_data.current_year_shares[1] !== 0
              ? `${numberWithCommas(Math.round(props.insider_trading_data.current_year_shares[1]))} to ${numberWithCommas(Math.round(props.insider_trading_data.current_year_shares[1] + props.insider_trading_data.current_year_shares[0]))}`
              : "0 transactions") + ")"}
          </p>
          {props.insider_trading_data.recent_report_link !== "No Insider Trading Links Available" ? (
            <p style={{ margin: 0 }}>
              <a href={props.insider_trading_data.recent_report_link} target="_blank" rel="noopener noreferrer">
                Click here for the most recent SEC filing.
              </a>
            </p>
          ) : (
            <p style={{ margin: 0 }}>{props.insider_trading_data.recent_report_link}</p>
          )}
        </div>
      )}
      <svg
        ref={svgRef}
        style={props.small ? { height: "100%", width: "100%" } : { padding: "10px", height: "100%", width: "100%" }}
      />
      {!props.small && (
        <Tooltip
          id="d3-sentiment-tooltip"
          multiline
          data-html
          style={{ zIndex: 105, backgroundColor: "white", color: "black", boxShadow: "0 0 5px #D9D9D9" }}
        />
      )}
    </div>
  );
}
